import React, { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useReducedMotion } from "framer-motion";

// ======================================================
// REAL TIME MENU DATA
// ======================================================

const lunchMenus = [
  // ======================================================
  // CATERING TIFFEN MENUS
  // ======================================================

  {
    id: "tiffen-basic",
    name: "Traditional Tiffen Menu",
    type: "Breakfast Catering",
    description:
      "Kesari, Idly (3 pcs), Sambaar, Chutney and Medhu Vadai (1 pc).",
    pricePerPerson: 99,
    category: "catering",
    popular: true,
    prepTime: "Fresh Morning Preparation",
  },

  {
    id: "tiffen-premium",
    name: "Premium Tiffen Menu",
    type: "Breakfast Catering",
    description:
      "Kesari / Ashoka / Kasi Halwa, Idly (2 pcs), Pongal, Sambaar, Chutney, Tomato Chutney, Vadai (1 pc) and Mini Coffee.",
    pricePerPerson: 199,
    category: "catering",
    popular: true,
    prepTime: "Authentic South Indian Preparation",
  },

  // ======================================================
  // CATERING LUNCH MENUS
  // ======================================================

  {
    id: "traditional-lunch",
    name: "Traditional Banana Leaf Lunch",
    type: "Lunch Catering",
    description:
      "Kadala Paruppu Payasam, Curd Pachidi, Potato Curry, Koottu, Kadamba Sambaar, Rasam, Pickle, Vadai, Appalam, Sweet, Daal, Rice, Ghee, Thick Buttermilk and Water Bottle served on Banana Leaf.",
    pricePerPerson: 350,
    category: "catering",
    popular: true,
    prepTime: "Traditional Virundhu Style",
  },

  {
    id: "premium-virundhu",
    name: "Premium Virundhu Meals",
    type: "Lunch Catering",
    description:
      "Milk Payasam, Curd Pachidi, Sweet Pachidi, Kaara Curry, Coconut Curry, Aviyal, Pickle, Appalam, Sweet, Vadai, Sambaar, Rasam, Veththa Kozhambu, Daal, Ghee, Rice, Variety Rice, Thick Buttermilk and Water Bottle served on Banana Leaf.",
    pricePerPerson: 450,
    category: "catering",
    popular: true,
    prepTime: "Premium Feast Preparation",
  },

  // ======================================================
  // DAILY FOODS MENUS
  // ======================================================

  {
    id: "daily-foods-without-rice",
    name: "Daily Foods Without Rice",
    type: "Regular Daily Meals",
    description: "Sambaar, Rasam, Curry and Koottu.",
    pricePerPerson: 160,
    category: "dailyfoods",
    prepTime: "Fresh Daily Cooking",
  },

  {
    id: "daily-foods-with-rice",
    name: "Daily Foods With Rice",
    type: "Complete Meals",
    description:
      "Sambaar, Rasam, Curry, Koottu, Rice and Butter Milk.",
    pricePerPerson: 210,
    category: "dailyfoods",
    popular: true,
    prepTime: "Fresh Daily Cooking",
  },
];

// ======================================================
// MENUS COMPONENT
// ======================================================

const Menus = ({ onGoToService }) => {
  const prefersReducedMotion = useReducedMotion();

  const [activeTab, setActiveTab] = useState("catering");
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setIsMenuExpanded(false);
  }, []);

  const toggleMenuExpand = useCallback(() => {
    setIsMenuExpanded((prev) => !prev);
  }, []);

  const currentMenus = lunchMenus.filter(
    (menu) => menu.category === activeTab
  );

  const handleKeyDown = (e, serviceId, menuId = null) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onGoToService(serviceId, menuId);
    }
  };

  return (
    <section
      className="section menuSection premium"
      aria-labelledby="menu-title"
    >
      {/* ======================================================
          HEADER
      ====================================================== */}

      <div className="sectionHeader premium">
        <h2 id="menu-title" className="sectionTitle premium">
          Authentic South Indian Menus
        </h2>

        <p className="sectionSubtitle premium">
          Traditional catering services and freshly prepared daily meals
          made with authentic South Indian taste, hygiene and premium
          ingredients.
        </p>
      </div>

      {/* ======================================================
          TABS
      ====================================================== */}

      <div className="tabsContainer premium" role="tablist">
        <motion.button
          className={`tabBtn premium ${
            activeTab === "catering" ? "active" : ""
          }`}
          onClick={() => handleTabChange("catering")}
          role="tab"
          whileHover={!prefersReducedMotion ? { scale: 1.05 } : {}}
          whileTap={{ scale: prefersReducedMotion ? 1 : 0.98 }}
        >
          🍛 Catering Menus
        </motion.button>

        <motion.button
          className={`tabBtn premium ${
            activeTab === "dailyfoods" ? "active" : ""
          }`}
          onClick={() => handleTabChange("dailyfoods")}
          role="tab"
          whileHover={!prefersReducedMotion ? { scale: 1.05 } : {}}
          whileTap={{ scale: prefersReducedMotion ? 1 : 0.98 }}
        >
          🍱 Daily Foods
        </motion.button>
      </div>

      {/* ======================================================
          SHOW MORE BUTTON
      ====================================================== */}

      {currentMenus.length > 3 && (
        <div
          className="menuToggle"
          onClick={toggleMenuExpand}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleMenuExpand();
            }
          }}
          tabIndex={0}
          aria-expanded={isMenuExpanded}
          aria-controls="menu-grid"
          role="button"
        >
          {isMenuExpanded ? "Show Less Menus" : "View All Menus"}
        </div>
      )}

      {/* ======================================================
          MENU GRID
      ====================================================== */}

      <div
        id="menu-grid"
        className={`menuGrid premium ${
          isMenuExpanded ? "expanded" : ""
        }`}
        role="tabpanel"
      >
        {(isMenuExpanded
          ? currentMenus
          : currentMenus.slice(0, 3)
        ).map((menu, index) => (
          <motion.div
            key={menu.id}
            className={`menuCard premium ${
              menu.popular ? "popular" : ""
            } ${menu.category}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            whileHover={
              !prefersReducedMotion
                ? {
                    y: -8,
                    scale: 1.01,
                    boxShadow: "0 15px 30px rgba(0,0,0,0.12)",
                  }
                : {}
            }
            transition={{ delay: index * 0.1 }}
            role="article"
            tabIndex={0}
          >
            {/* ======================================================
                CARD HEADER
            ====================================================== */}

            <div className="menuHeader premium">
              <h3>{menu.name}</h3>

              <span
                className={`menuType premium ${menu.category}`}
              >
                {menu.type}
              </span>
            </div>

            {/* ======================================================
                DESCRIPTION
            ====================================================== */}

            <p className="menuDescription premium">
              {menu.description}
            </p>

            {/* ======================================================
                PREP TIME
            ====================================================== */}

            <div className="menuMeta premium">
              <span className="prepTime">
                ⏱️ {menu.prepTime}
              </span>
            </div>

            {/* ======================================================
                PRICE SECTION
            ====================================================== */}

            <div className="menuPriceSection premium">
              <div className="menuPrice premium">
                <span className="price premium">
                  ₹{menu.pricePerPerson}
                </span>

                <span className="perPerson premium">
                  per person
                </span>
              </div>

              {menu.popular && (
                <motion.div
                  className="popularLabel premium"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  Popular Choice
                </motion.div>
              )}
            </div>

            {/* ======================================================
                ACTION BUTTONS
            ====================================================== */}

            <div className="menuActions premium">
              <motion.button
                className="primaryBtn smallBtn premium"
                onClick={() =>
                  onGoToService(
                    activeTab === "catering"
                      ? "catering"
                      : "CloudKitchen",
                    menu.id
                  )
                }
                onKeyDown={(e) =>
                  handleKeyDown(
                    e,
                    activeTab === "catering"
                      ? "catering"
                      : "CloudKitchen",
                    menu.id
                  )
                }
                whileHover={
                  !prefersReducedMotion
                    ? { scale: 1.08 }
                    : {}
                }
                whileTap={{
                  scale: prefersReducedMotion ? 1 : 0.96,
                }}
              >
                {activeTab === "catering"
                  ? "Book Catering"
                  : "Order Meals"}
              </motion.button>

              <motion.button
                className="secondaryBtn smallBtn premium"
                onClick={() =>
                  onGoToService(
                    activeTab === "catering"
                      ? "ChefBooking"
                      : "CloudKitchen",
                    menu.id
                  )
                }
                whileHover={
                  !prefersReducedMotion
                    ? { scale: 1.08 }
                    : {}
                }
                whileTap={{
                  scale: prefersReducedMotion ? 1 : 0.96,
                }}
              >
                {activeTab === "catering"
                  ? "Private Chef"
                  : "View Details"}
              </motion.button>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default Menus;